// services/entitiesServices.ts
import { BaseService } from './baseService';
import { apiClient } from '../config/api';
import { AuthService } from './authService';
import { ENDPOINTS } from '../config/endpoints';
import type {
  Usuario,
  Empresa,
  Grupo,
  Cliente,
  Seguradora,
  Produto,
  FormaPagamento,
  Status,
  Situacao,
  Documento,
  Parcela,
  Agendamento,
  CalculoComissao,
  ImportacaoResultado
} from '../types/entities';

// ✅ UsuarioService
export class UsuarioService extends BaseService<Usuario> {
  protected endpoints = ENDPOINTS.USUARIOS;

  // Método específico para buscar por email
  async getByEmail(email: string) {
    return apiClient.request<Usuario>(`${ENDPOINTS.USUARIOS.BY_EMAIL}/${email}`);
  }

  // Método específico para atualizar senha
  async updatePassword(id: string | number, senhaAtual: string, novaSenha: string) {
    return apiClient.request(ENDPOINTS.USUARIOS.UPDATE_PASSWORD(id), {
      method: 'PUT',
      body: JSON.stringify({ senhaAtual, novaSenha }),
    });
  }
}

// ✅ EmpresaService
export class EmpresaService extends BaseService<Empresa> {
  protected endpoints = ENDPOINTS.EMPRESAS;
  
  // Buscar empresas por grupo (usa endpoint específico se disponível)
  async listByGrupo(grupoId: string | number) {
    return apiClient.request<any>(ENDPOINTS.EMPRESAS.BY_GRUPO(grupoId));
  }
}

// ✅ GrupoService
export class GrupoService extends BaseService<Grupo> {
  protected endpoints = ENDPOINTS.GRUPOS;
}

// ✅ ClienteService
export class ClienteService extends BaseService<Cliente> {
  protected endpoints = ENDPOINTS.CLIENTES;
}

// ✅ SeguradoraService
export class SeguradoraService extends BaseService<Seguradora> {
  protected endpoints = ENDPOINTS.CATALOGS.SEGURADORAS;
  // Some backends expect empresaId in the request when validating access for catalog deletes
  async delete(id: string | number) {
    const empresaId = AuthService.getEmpresaId();
    const body = empresaId ? { empresaId } : undefined;
    return apiClient.request<any>(this.endpoints.DELETE(id), {
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    });
  }
}

// ✅ ProdutoService
export class ProdutoService extends BaseService<Produto> {
  protected endpoints = ENDPOINTS.CATALOGS.PRODUTOS;
}

// ✅ FormaPagamentoService
export class FormaPagamentoService extends BaseService<FormaPagamento> {
  protected endpoints = ENDPOINTS.CATALOGS.PAGAMENTOS;
}

// ✅ StatusService
export class StatusService extends BaseService<Status> {
  protected endpoints = ENDPOINTS.CATALOGS.STATUS;
}

// ✅ SituacaoService
export class SituacaoService extends BaseService<Situacao> {
  protected endpoints = ENDPOINTS.CATALOGS.SITUACOES;
}

// ✅ DocumentoService
export class DocumentoService extends BaseService<Documento> {
  protected endpoints = ENDPOINTS.DOCUMENTOS;
}

// ✅ ParcelaService
export class ParcelaService extends BaseService<Parcela> {
  protected endpoints = ENDPOINTS.PARCELAS;
}

// ✅ AgendamentoService
export class AgendamentoService extends BaseService<Agendamento> {
  protected endpoints = ENDPOINTS.AGENDAMENTOS;
}

// ✅ CalculoComissaoService
export class CalculoComissaoService extends BaseService<CalculoComissao> {
  protected endpoints = ENDPOINTS.CALCULO;

  // Método específico para calcular comissão
  async calcularComissao(data: {
    dataInicio: string;
    dataFim: string;
    tipoCalculo: 'producao' | 'pagamento';
  }) {
    return apiClient.request<CalculoComissao>(ENDPOINTS.CALCULO.COMISSAO, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}

// ✅ ImportacaoService
export class ImportacaoService {
  // Importar lista de clientes
  async importarClientes(file: File): Promise<ImportacaoResultado> {
  const formData = new FormData();
  // backend expects the file field to be named 'arquivo'
  formData.append('arquivo', file);

    return apiClient.request<ImportacaoResultado>(ENDPOINTS.IMPORTACAO.CLIENTE, {
      method: 'POST',
      body: formData,
    });
  }

  // Importar análise de produção
  async importarAnalise(file: File): Promise<ImportacaoResultado> {
  const formData = new FormData();
  // backend expects the file field to be named 'arquivo'
  formData.append('arquivo', file);

    return apiClient.request<ImportacaoResultado>(ENDPOINTS.IMPORTACAO.ANALISE, {
      method: 'POST',
      body: formData,
    });
  }

  // Importar multi-cálculo
  async importarMultCalculo(file: File): Promise<ImportacaoResultado> {
    console.log('🔧 importarMultCalculo - Arquivo recebido:', file);
    console.log('🔧 Tamanho do arquivo:', file.size, 'bytes');
    console.log('🔧 Tipo do arquivo:', file.type);
    
    const formData = new FormData();
    // backend expects the file field to be named 'arquivo'
    formData.append('arquivo', file);
    
    console.log('🔧 FormData criado com arquivo anexado');
    console.log('🔧 Endpoint:', ENDPOINTS.IMPORTACAO.MULTCALCULO);

    return apiClient.request<ImportacaoResultado>(ENDPOINTS.IMPORTACAO.MULTCALCULO, {
      method: 'POST',
      body: formData,
    });
  }

  // Importar propostas pendentes
  async importarPropostas(file: File): Promise<ImportacaoResultado> {
  const formData = new FormData();
  // backend expects the file field to be named 'arquivo'
  formData.append('arquivo', file);

    return apiClient.request<ImportacaoResultado>(ENDPOINTS.IMPORTACAO.PROPOSTAS, {
      method: 'POST',
      body: formData,
    });
  }

  // Importar arquivo CSV de migração (MS Access) - Upload + Importação em uma chamada
  async importarMigracao(file: File, tableName?: string): Promise<ImportacaoResultado> {
    const formData = new FormData();
    formData.append('arquivo', file);
    if (tableName) {
      // enviar ambos nomes para compatibilidade com diferentes backends
      formData.append('tableName', tableName);
      formData.append('table', tableName);
    }

    // Dev-only: log FormData contents (file name and keys)
    try {
      // @ts-ignore - FormData entries might not be fully typed in all environments
      const keys: string[] = [];
      // modern browsers support formData.keys()
      if (typeof (formData as any).keys === 'function') {
        for (const k of (formData as any).keys()) keys.push(k);
      }
      // eslint-disable-next-line no-console
      console.log('ImportacaoService.importarMigracao -> formData keys:', keys, 'fileName:', file.name, 'tableName:', tableName);
    } catch (e) {
      // ignore logging errors
    }

    return apiClient.request<ImportacaoResultado>(ENDPOINTS.IMPORTACAO.ACCESS.UPLOAD_IMPORT, {
      method: 'POST',
      body: formData,
    });
  }
}

// ✅ Instâncias dos serviços
export const usuarioService = new UsuarioService();
export const empresaService = new EmpresaService();
export const grupoService = new GrupoService();
export const clienteService = new ClienteService();
export const seguradoraService = new SeguradoraService();
export const produtoService = new ProdutoService();
export const formaPagamentoService = new FormaPagamentoService();
export const statusService = new StatusService();
export const situacaoService = new SituacaoService();
export const documentoService = new DocumentoService();
export const parcelaService = new ParcelaService();
export const agendamentoService = new AgendamentoService();
export const calculoComissaoService = new CalculoComissaoService();
export const importacaoService = new ImportacaoService();